import { memo, useMemo } from "react";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { useOrderBookTick, useSnapshotTick } from "../../hooks/useSecurityMarketTicks";
import { toLocaleStringIn } from "../../util/general";
import { useStyles } from "./styles";
import { selectOrderType, selectTradeOrders } from "../../selectors/strategyBuilder";
import { AnalyzeGroupedPosition } from "../../reducer/positions.reducer";
import { Broker_OrderType } from "../../proto/generated/order";
import { Typography } from "@mui/material";

export type ProfitCellProps = {
    position: AnalyzeGroupedPosition
}

const ProfitCell: React.FC<ProfitCellProps> = ({position}) => {

    const {classes, cx} = useStyles();
    const securityKey = getSecurityKey(position.security.exchange, position.security.code);
    const snapshot = useSnapshotTick(securityKey)
    const orderBook = useOrderBookTick(securityKey, true)
    const tradeOrders = useSelector(selectTradeOrders);
    const orderType = useSelector(selectOrderType);

    const squareOffPrice = useMemo(() => {
        const {squareOffOrderId} = position;
        const squareOffOrder = tradeOrders?.find(t => t.clientOrderId === squareOffOrderId);

        // If there's a square off order with a price and we're in LIMIT mode, use the fixed price
        // If in MARKET mode, use order book price (bid for selling/squaring off long, ask for buying/squaring off short)
        return squareOffOrder && squareOffOrder.selected
            ? (squareOffOrder.price && squareOffOrder.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER
                ? squareOffOrder.price
                : (position.originalPosition.closePosition > 0
                    ? (orderBook?.bids[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                    : (orderBook?.asks[0]?.price ?? snapshot?.lastTradedPrice ?? 0)))
            : 0

    }, [position, snapshot, orderBook, tradeOrders, orderType])

    const { realizedProfit, unrealizedProfit } = useMemo(() => {
        const { security, avgPrice, newQuantity, squaredOffQuantity, addedQuantity } = position;
        if (position.children?.length > 1) return { realizedProfit: 0, unrealizedProfit: 0 };

        const priceMultiplier = security.priceMultiplier ?? 100;

        // -------------------------
        // REALIZED PROFIT
        // -------------------------
        let realizedProfit = position.realizedProfit || 0;
        if (squaredOffQuantity !== 0) {
            const originalPosition = position.originalPosition;
            const direction = originalPosition.closePosition > 0 ? 1 : -1;
            const originalAvgPrice = originalPosition.closePositionValue/originalPosition.closePosition;
            realizedProfit += direction * squaredOffQuantity*(squareOffPrice - originalAvgPrice)
        }
        realizedProfit = realizedProfit / priceMultiplier;

        // -------------------------
        // UNREALIZED PROFIT
        // -------------------------
        let unrealizedProfit = 0;
        if (newQuantity !== 0 && snapshot) {
            // For incoming positions (addedQuantity !== 0), use LTP for both entry and current price
            // This makes profit = 0 for incoming positions (visual only, doesn't affect order placement)
            const isIncoming = (addedQuantity ?? 0) !== 0;
            let entryPrice = avgPrice;

            if (isIncoming) {
                // For incoming positions, use current LTP as entry price so profit = 0
                entryPrice = snapshot.lastTradedPrice;
            }

            unrealizedProfit = ((snapshot.lastTradedPrice - entryPrice) * newQuantity) / priceMultiplier;
        }

        return { realizedProfit, unrealizedProfit };
    }, [position, snapshot, squareOffPrice]);

    // If position is completely squared off, only show realized profit
    if (position.newQuantity === 0) {
        return <span className={cx(
            realizedProfit > 0 ? classes.profitColor : undefined,
            realizedProfit < 0 ? classes.lossColor : undefined
        )}>{toLocaleStringIn(realizedProfit)}</span>;
    }

    // For positions with remaining quantity, show unrealized profit as main text
    // and realized profit in footer (if any)
    return (
        <>
            <Typography
                variant="body2"
                className={cx(
                    unrealizedProfit > 0 ? classes.profitColor : undefined,
                    unrealizedProfit < 0 ? classes.lossColor : undefined
                )}
                style={{ textAlign: 'right' }}
            >
                {toLocaleStringIn(unrealizedProfit ?? 0)}
            </Typography>
        </>
    );
}


export default memo(ProfitCell);